/**
 * FloodBrief — the headline-risk panel for the FLOOD lens.
 *
 * Shows:
 *  • Pattani River / canal gauge status (worst-case status drives the colour)
 *  • Bang Lang Dam storage % + outflow (rising outflow precedes city flooding)
 *  • Rainfall nowcast (next 2 h) from the precip feed
 *  • Flood-risk household exposure summed from the hand-authored polygons
 *
 * All data is live where a feed exists; the household figure is reference
 * (hand-authored). Degrades gracefully when feeds are unavailable.
 */

import { useMemo } from "react";
import type { Feature, Polygon, MultiPolygon } from "geojson";
import type { FloodGauge, DamStatus, PattaniRiverForecast, PrecipNowcast, Shelter, FallbackTier } from "@yala/shared";
import { PanelHeader } from "./PanelHeader";
import { useT } from "../hooks/useLang";
import { useGaugeHistory } from "../hooks/useGaugeHistory";
import { FloodWatchProfile } from "./FloodWatchProfile";

interface Props {
  gauges: FloodGauge[];
  dam: DamStatus | null;
  precip: PrecipNowcast | null;
  floodRiskFeatures: Array<Feature<Polygon | MultiPolygon, Record<string, unknown>>> | null;
  riverForecast?: PattaniRiverForecast | null;
  shelters?: Shelter[];
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
}

const GAUGE_STATUS_COLOR: Record<FloodGauge["status"], string> = {
  normal: "var(--good)",
  watch: "var(--warn)",
  warning: "var(--warn)",
  flood: "var(--bad)",
  unknown: "var(--text-3)",
};

const GAUGE_RANK: Record<FloodGauge["status"], number> = {
  normal: 0,
  watch: 1,
  warning: 2,
  flood: 3,
  unknown: -1,
};

const DAM_STATUS_COLOR: Record<DamStatus["status"], string> = {
  low: "var(--data)",
  normal: "var(--good)",
  high: "var(--warn)",
  spilling: "var(--bad)",
  unknown: "var(--text-3)",
};

const INTENSITY_COLOR: Record<PrecipNowcast["intensity"], string> = {
  dry: "var(--good)",
  light: "var(--data)",
  moderate: "var(--warn)",
  heavy: "var(--bad)",
};

const REACH_BAND_COLOR: Record<PattaniRiverForecast["band"], string> = {
  normal: "var(--good)",
  watch: "var(--warn)",
  warning: "var(--warn)",
  emergency: "var(--bad)",
  unknown: "var(--text-3)",
};

const TREND_ARROW: Record<PattaniRiverForecast["trend"], string> = {
  rising: "▲",
  falling: "▼",
  stable: "→",
  unknown: "—",
};

function fmt(n: number | null | undefined, digits = 1, unit = ""): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${n.toFixed(digits)}${unit}`;
}

export function FloodBrief({ gauges, dam, precip, floodRiskFeatures, riverForecast, shelters, ageMinutes, fallbackTier }: Props) {
  const t = useT();
  const { history } = useGaugeHistory(gauges);
  const worstGauge = useMemo(() => {
    if (!gauges.length) return null;
    return [...gauges].sort((a, b) => GAUGE_RANK[b.status] - GAUGE_RANK[a.status])[0];
  }, [gauges]);

  const exposedHouseholds = useMemo(() => {
    if (!floodRiskFeatures?.length) return null;
    return floodRiskFeatures.reduce((sum, f) => {
      const h = Number(f.properties?.households ?? 0);
      return sum + (Number.isFinite(h) ? h : 0);
    }, 0);
  }, [floodRiskFeatures]);

  return (
    <div className="col flood-brief">
      <PanelHeader
        title={t("FLOOD BRIEF // PATTANI RIVER", "สถานการณ์น้ำท่วม // แม่น้ำปัตตานี")}
        source="flooddash / rid / open-meteo"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier}
      />

      {/* ── FLOOD WATCH · GOD'S MODE ─────────────────────────────────────────
          The river profile chart: dam → gauges → forecast, all in one SVG.
          Replaces the discrete "worst gauge" KPI row above with a holistic
          view of the entire Pattani River basin. The KPIs above (dam status,
          outflow, rain, exposure) remain for fast scanning; the profile
          below is for "show me where the water is, and where it's going". */}
      <FloodWatchProfile
        gauges={gauges}
        dam={dam}
        forecast={riverForecast ?? null}
        shelters={shelters ?? []}
        history={history}
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier}
      />

      {/* River gauge status — kept as a fast-scanning summary for the operator
          who just wants the worst gauge and count above warning. The profile
          above is the spatial view; this is the at-a-glance number. */}
      <div className="flood-row" style={{ display: "flex", gap: 12, marginTop: 8 }}>
        <div style={{ flex: 1 }}>
          <div className="eyebrow">RIVER GAUGE</div>
          {worstGauge ? (
            <>
              <div className="mono" style={{ color: GAUGE_STATUS_COLOR[worstGauge.status], fontSize: "0.9rem" }}>
                {worstGauge.status.toUpperCase()}
              </div>
              <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
                {worstGauge.name} · {fmt(worstGauge.levelM, 2, " m")}
                {worstGauge.warningM != null ? ` / ${fmt(worstGauge.warningM, 1, " m")}` : ""}
              </div>
            </>
          ) : (
            <div className="mono eyebrow" style={{ color: "var(--text-3)" }}>no gauge feed</div>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <div className="eyebrow">GAUGES</div>
          <div className="mono" style={{ fontSize: "0.9rem" }}>{gauges.length || "—"}</div>
          <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
            {gauges.filter((g) => g.status === "warning" || g.status === "flood").length} above warning
          </div>
        </div>
      </div>

      {/* Bang Lang Dam */}
      <div className="flood-row" style={{ display: "flex", gap: 12, marginTop: 10 }}>
        <div style={{ flex: 1 }}>
          <div className="eyebrow">BANG LANG DAM</div>
          <div className="mono" style={{ color: dam ? DAM_STATUS_COLOR[dam.status] : "var(--text-3)", fontSize: "0.9rem" }}>
            {dam ? dam.status.toUpperCase() : "—"}
          </div>
          <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
            storage {fmt(dam?.storagePct, 0, "%")}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div className="eyebrow">OUTFLOW</div>
          <div className="mono" style={{ fontSize: "0.9rem" }}>{fmt(dam?.outflowCms, 0, " m³/s")}</div>
          <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
            rising outflow precedes city flooding
          </div>
        </div>
      </div>

      {/* Rainfall nowcast */}
      <div className="flood-row" style={{ display: "flex", gap: 12, marginTop: 10 }}>
        <div style={{ flex: 1 }}>
          <div className="eyebrow">RAIN NOW</div>
          <div
            className="mono"
            style={{ color: precip ? INTENSITY_COLOR[precip.intensity] : "var(--text-3)", fontSize: "0.9rem" }}
          >
            {fmt(precip?.nowMm, 1, " mm")}
          </div>
          <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
            {precip ? precip.intensity.toUpperCase() : "—"}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div className="eyebrow">NEXT 2H</div>
          <div className="mono" style={{ fontSize: "0.9rem" }}>{fmt(precip?.total2hMm, 1, " mm")}</div>
          <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
            {precip?.minutesToSignificant != null ? `rain in ~${precip.minutesToSignificant} min` : "no rain forecast"}
          </div>
        </div>
      </div>

      {/* Household exposure */}
      {exposedHouseholds != null && (
        <div style={{ marginTop: 10 }}>
          <div className="eyebrow">FLOOD-RISK EXPOSURE</div>
          <div className="mono" style={{ fontSize: "1.0rem", color: "var(--warn)" }}>
            ~{exposedHouseholds.toLocaleString()} households
          </div>
          <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
            across {floodRiskFeatures?.length ?? 0} riverside zones · reference
          </div>
        </div>
      )}

      {/* River forecast — deep-dive detail, collapsed by default (native <details>,
          no state hook — same pattern as BuildingLegend). Most viewers only need
          the headline gauge/dam status above; this is for whoever wants the
          GloFAS-modelled 48h peak discharge. */}
      {riverForecast && (
        <details className="detail-disclosure">
          <summary className="mono">{t("River forecast detail", "รายละเอียดพยากรณ์แม่น้ำ")}</summary>
          <div className="detail-disclosure-body">
            <div className="flood-row" style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div className="eyebrow">{t("DISCHARGE NOW", "อัตราการไหลปัจจุบัน")}</div>
                <div className="mono" style={{ fontSize: "0.9rem" }}>{fmt(riverForecast.dischargeCms, 1, " m³/s")}</div>
                <div className="eyebrow mono" style={{ color: REACH_BAND_COLOR[riverForecast.band] }}>
                  {TREND_ARROW[riverForecast.trend]} {riverForecast.trend.toUpperCase()} · {riverForecast.band.toUpperCase()}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div className="eyebrow">{t("48H FORECAST PEAK", "จุดสูงสุดคาดการณ์ 48 ชม.")}</div>
                <div className="mono" style={{ fontSize: "0.9rem" }}>{fmt(riverForecast.forecastPeakCms, 1, " m³/s")}</div>
                <div className="eyebrow mono" style={{ color: "var(--text-3)" }}>
                  {riverForecast.forecastPeakDay != null
                    ? t(`in ~${riverForecast.forecastPeakDay}d`, `ในอีก ~${riverForecast.forecastPeakDay} วัน`)
                    : t("no peak forecast", "ไม่มีข้อมูลพยากรณ์")}
                </div>
              </div>
            </div>
            <div className="eyebrow mono" style={{ color: "var(--text-3)", marginTop: 6 }}>
              {t(
                `${riverForecast.name} · GloFAS model, below Bang Lang Dam`,
                `${riverForecast.name} · แบบจำลอง GloFAS ท้ายเขื่อนบางลาง`,
              )}
            </div>
          </div>
        </details>
      )}

      <div className="eyebrow mono" style={{ color: "var(--text-3)", marginTop: 8 }}>
        PATTANI RIVER · BANG LANG DAM (BANNANG SATA) — flood is Yala's headline risk
      </div>
    </div>
  );
}
